/**
 * Modale « évolution d'une stat » ouverte au CLIC sur une cellule de stat de la
 * page Statistiques. Le facteur d'évolution dépend de la table :
 *  - MATCH   → évolution INTRA-match (minute par minute, `statGraph`, dom/ext).
 *  - ÉQUIPE  → la stat MATCH PAR MATCH du tournoi (une courbe, x = adversaires).
 *  - JOUEUR  → la stat MATCH PAR MATCH (player-history), une courbe.
 *  - GROUPE  → comment la stat se DISPATCHE sur les nations : une courbe par nation,
 *    x = journées (J1→J3).
 * Réutilise la coquille `.md-overlay` (comme les fiches) ; data only, fonctions
 * d'extraction PURES (testées).
 */
import type { MatchView, OfficialState } from "../app/officialStore.ts";
import { fetchStatHistory, pluckSeries } from "../app/statsHistory.ts";
import { fetchPlayerHistory, type PlayerMatchRow } from "../app/playerData.ts";
import { statGraph } from "./components/statGraph.ts";
import { seriesChart, type ChartSeries } from "./components/seriesChart.ts";
import { esc, flagSpan, teamName, frontZ } from "./html.ts";
import { t } from "../app/i18n.ts";

export type EvKind = "group" | "team" | "match" | "player";
export interface EvContext {
  readonly kind: EvKind;
  readonly statKey: string;
  readonly label: string; // libellé lisible de la stat (depuis la colonne)
  readonly group?: string;
  readonly code?: string; // équipe
  readonly id?: number; // match / joueur
  readonly name?: string; // joueur
}

const PCT_KEYS = new Set(["possession", "passAcc", "duelWinPct"]);
const num = (x: unknown): number | null => (typeof x === "number" && Number.isFinite(x) ? x : null);

/** Côté (0=dom, 1=ext) d'une équipe dans un match — KO résolu via le bracket. */
function teamSide(state: OfficialState, v: MatchView, code: string): 0 | 1 | null {
  if (v.match.stage === "group") return v.match.home === code ? 0 : v.match.away === code ? 1 : null;
  const node = state.bracket["M" + String(v.match.id)];
  if (node?.a === code) return 0;
  if (node?.b === code) return 1;
  return null;
}

/** Valeur d'une stat d'ÉQUIPE dans un match (côté `s`). null si indisponible. */
export function teamStatInMatch(v: MatchView, s: 0 | 1, statKey: string): number | null {
  const sc = v.displayScore ?? v.live?.score?.ft ?? null;
  const st = v.live?.stats ?? null;
  const o: 0 | 1 = s === 0 ? 1 : 0;
  const pick = (k: string, side: 0 | 1): number | null => num(st?.[k]?.[side]);
  switch (statKey) {
    case "goals":
    case "goalsFor":
      return sc ? num(sc[s]) : null;
    case "goalsAgainst":
      return sc ? num(sc[o]) : null;
    case "diff":
      return sc ? (num(sc[s]) ?? 0) - (num(sc[o]) ?? 0) : null;
    case "points": {
      if (!sc) return null;
      const gf = num(sc[s]) ?? 0;
      const ga = num(sc[o]) ?? 0;
      return gf > ga ? 3 : gf < ga ? 0 : 1;
    }
    case "minutes":
      return v.penalties != null || v.match.stage !== "group" ? 120 : 90;
    case "xga":
      return pick("xg", o);
    case "shots":
      return pick("shots", s);
    case "shotsOn":
      return pick("shotsOnTarget", s);
    case "cards":
      return (pick("yellow", s) ?? 0) + (pick("red", s) ?? 0);
    case "xg":
    case "possession":
    case "passAcc":
    case "corners":
      return pick(statKey, s);
    default:
      return pick(statKey, s);
  }
}

/** Valeur d'une stat de JOUEUR sur un match (player-history). */
export function playerStatInMatch(r: PlayerMatchRow, statKey: string): number | null {
  switch (statKey) {
    case "goals":
      return r.goals;
    case "assists":
      return r.assists;
    case "minutes":
      return num(r.minutes);
    case "rating":
      return num(r.rating);
    case "shots":
      return num(r.shots);
    case "shotsOn":
      return num(r.shotsOn);
    case "passes":
      return num(r.passes);
    case "keyPasses":
      return num(r.keyPasses);
    case "passAcc":
      return num(r.passAcc);
    case "duelWinPct":
      return r.duelsTotal ? ((r.duelsWon ?? 0) / r.duelsTotal) * 100 : null;
    default:
      return null;
  }
}

/** Matchs d'une équipe (groupes + KO), dans l'ordre chronologique, avec son côté. */
function teamMatches(state: OfficialState, code: string): { v: MatchView; s: 0 | 1 }[] {
  return state.matches
    .map((v) => ({ v, s: teamSide(state, v, code) }))
    .filter((x): x is { v: MatchView; s: 0 | 1 } => x.s !== null && (x.v.status === "finished" || x.v.status === "live" || x.v.status === "paused"))
    .sort((a, b) => Date.parse(a.v.match.utcDateTime) - Date.parse(b.v.match.utcDateTime));
}

/** Série ÉQUIPE : la stat match par match (x = adversaire). */
export function teamSeries(state: OfficialState, code: string, statKey: string): { x: string[]; series: ChartSeries[] } {
  const ms = teamMatches(state, code);
  const x = ms.map(({ v, s }) => {
    const opp = s === 0 ? v.match.away : v.match.home;
    const node = v.match.stage !== "group" ? state.bracket["M" + String(v.match.id)] : null;
    const oppCode = node ? (s === 0 ? node.b : node.a) : opp;
    return oppCode && /^[A-Z]{3}$/.test(oppCode) ? oppCode : "?";
  });
  const points = ms.map(({ v, s }, i) => ({ x: x[i] ?? "?", y: teamStatInMatch(v, s, statKey) }));
  return { x, series: [{ label: teamName(code), points }] };
}

/** Série GROUPE : une courbe par nation, x = journées (la stat dispatchée). */
export function groupSeries(state: OfficialState, group: string, statKey: string): { x: string[]; series: ChartSeries[] } {
  const gm = state.matches.filter((v) => v.match.stage === "group" && v.match.group === group);
  const mds = [...new Set(gm.map((v) => v.match.matchday).filter((n): n is number => n != null))].sort((a, b) => a - b);
  const nations = [...new Set(gm.flatMap((v) => [v.match.home, v.match.away]))].sort();
  const x = mds.map((md) => `${t("stats.col.played.abbr")}${md}`);
  const series: ChartSeries[] = nations.map((code) => ({
    label: code,
    points: mds.map((md) => {
      const v = gm.find((m) => m.match.matchday === md && (m.match.home === code || m.match.away === code));
      if (!v || (v.status !== "finished" && v.status !== "live" && v.status !== "paused")) return { x: `${md}`, y: null };
      const s: 0 | 1 = v.match.home === code ? 0 : 1;
      return { x: `${md}`, y: teamStatInMatch(v, s, statKey) };
    }),
  }));
  return { x, series };
}

// --- overlay (coquille .md-overlay) ---------------------------------------
let ov: HTMLElement | null = null;
function ensureOverlay(): HTMLElement {
  if (ov) return ov;
  ov = document.createElement("div");
  ov.className = "md-overlay se-overlay hide";
  ov.innerHTML = `<div class="md-card se-card" role="dialog" aria-modal="true"><button type="button" class="md-close" aria-label="${esc(t("fiche.closeProfile"))}"><i class="ti ti-x" aria-hidden="true"></i></button><div class="md-body se-body"></div></div>`;
  const close = (): void => {
    ov?.classList.remove("open");
    ov?.classList.add("hide");
  };
  ov.addEventListener("click", (e) => {
    if (e.target === ov || (e.target as Element).closest(".md-close")) close();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && ov && !ov.classList.contains("hide")) close();
  });
  document.body.appendChild(ov);
  return ov;
}

function titleHtml(ctx: EvContext, sub: string, code?: string): string {
  const who = code ? `${flagSpan(code, { name: true })}` : ctx.kind === "group" ? `${t("stats.section.groups")} ${esc(ctx.group ?? "")}` : esc(ctx.name ?? "");
  return `<div class="se-head"><div class="se-who">${who}</div><div class="se-stat">${esc(ctx.label)}</div><div class="se-sub note">${esc(sub)}</div></div>`;
}

/** Ouvre la modale d'évolution pour une cellule de stat. */
export function openStatEvolution(state: OfficialState, ctx: EvContext): void {
  const el = ensureOverlay();
  const body = el.querySelector<HTMLElement>(".se-body");
  if (!body) return;
  el.classList.remove("hide");
  el.style.zIndex = String(frontZ());
  void el.offsetWidth; // reflow → la transition d'ouverture joue, sans dépendre du rAF
  el.classList.add("open");
  const asPct = PCT_KEYS.has(ctx.statKey);
  const loading = `<div class="sc-empty">${esc(t("common.loading"))}</div>`;

  if (ctx.kind === "match" && ctx.id != null) {
    const v = state.byId.get(ctx.id);
    const live = v?.status === "live" || v?.status === "paused";
    const node = v && v.match.stage !== "group" ? state.bracket["M" + String(ctx.id)] : null;
    const home = (node?.a ?? v?.match.home) ?? "DOM";
    const away = (node?.b ?? v?.match.away) ?? "EXT";
    const goals = (v?.live?.events ?? [])
      .filter((e) => e.type === "goal" && typeof e.minute === "number")
      .map((e) => ({ minute: e.minute as number, team: e.team }));
    body.innerHTML = titleHtml(ctx, t("stats.ev.inMatch")) + loading;
    void fetchStatHistory(ctx.id, { live }).then((s) => {
      if (el.classList.contains("hide")) return;
      const chart = statGraph(pluckSeries(s, ctx.statKey), ctx.statKey, home, away, goals, live);
      body.innerHTML = titleHtml(ctx, t("stats.ev.inMatch")) + `<div class="se-chart">${chart}</div>`;
    });
    return;
  }

  if (ctx.kind === "team" && ctx.code) {
    const { x, series } = teamSeries(state, ctx.code, ctx.statKey);
    body.innerHTML =
      titleHtml(ctx, t("stats.ev.perMatch"), ctx.code) +
      `<div class="se-chart">${seriesChart(x, series, { asPct, emptyMsg: t("stats.ev.noData") })}</div>`;
    return;
  }

  if (ctx.kind === "group" && ctx.group) {
    const { x, series } = groupSeries(state, ctx.group, ctx.statKey);
    body.innerHTML =
      titleHtml(ctx, t("stats.ev.perNation")) +
      `<div class="se-chart">${seriesChart(x, series, { asPct, emptyMsg: t("stats.ev.noData") })}</div>`;
    return;
  }

  if (ctx.kind === "player" && ctx.id != null) {
    body.innerHTML = titleHtml(ctx, t("stats.ev.perMatch")) + loading;
    void fetchPlayerHistory(ctx.id).then((h) => {
      if (el.classList.contains("hide")) return;
      const rows = [...(h?.matches ?? [])].sort((a, b) => a.matchId - b.matchId);
      const x = rows.map((r) => (r.oppCode && /^[A-Z]{3}$/.test(r.oppCode) ? r.oppCode : `#${r.matchId}`));
      const points = rows.map((r, i) => ({ x: x[i] ?? "?", y: playerStatInMatch(r, ctx.statKey) }));
      body.innerHTML =
        titleHtml(ctx, t("stats.ev.perMatch")) +
        `<div class="se-chart">${seriesChart(x, [{ label: ctx.name ?? "", points }], { asPct, emptyMsg: t("stats.ev.noData") })}</div>`;
    });
    return;
  }

  el.classList.add("hide");
}
